import type { EntryCta } from '@funkit/connect-core'
import { submittableTransition } from '@funkit/connect-core'
import {
  type CountryCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/min'

/** Where the one-time code is sent, which decides keyboard and copy. */
export type ContactKind = 'PHONE' | 'EMAIL'

export interface SessionContactField {
  /** The transition's own input name — the key the answer is sent back under. */
  name: string
  kind: ContactKind
  /** The server's pattern for the value, when it declares one. */
  regex?: string
}

/**
 * Every value the session-opening transition asks the user for, in the order
 * it declares them.
 *
 * All of them: a step that wants a number *and* an address verifies each on a
 * round of its own, and collecting only the first opens a session the server
 * cannot finish. The name is the body key, so gating on a known name is how
 * the client stops sending a field the server asked for.
 */
export function sessionContactFields(
  entry: EntryCta,
): readonly SessionContactField[] {
  const transition = submittableTransition(entry)
  return Object.entries(transition?.inputs ?? {})
    .filter(([, spec]) => spec.type === 'TEXT')
    .map(([name, spec]) => ({
      name,
      kind: contactKind(name),
      ...(spec.type === 'TEXT' && spec.regex !== undefined
        ? { regex: spec.regex }
        : {}),
    }))
}

/**
 * Presentation only — the contract types both plain `TEXT`, so the name is the
 * one hint about which keyboard to raise. An unrecognised name still collects;
 * it just gets the email treatment.
 */
function contactKind(name: string): ContactKind {
  return PHONE_NAMES.has(name) ? 'PHONE' : 'EMAIL'
}

const PHONE_NAMES = new Set(['phone', 'phoneNumber', 'mobileNumber'])

/** Names that need no fallback warning — the ones we have seen served. */
const KNOWN_NAMES = new Set(['email', 'emailAddress', ...PHONE_NAMES])

export function isUnfamiliarContactName(name: string): boolean {
  return !KNOWN_NAMES.has(name)
}

/**
 * Nothing opens the session until every field it declares is answered.
 * `region` is the buyer's country, so a number typed the way they dial it at
 * home counts once it can be read as one.
 */
export function areContactsComplete(
  fields: readonly SessionContactField[],
  values: Readonly<Record<string, string>>,
  region?: string,
): boolean {
  return fields.every((field) =>
    isCompleteContact(field, values[field.name] ?? '', region),
  )
}

/**
 * A number has to be one the world's numbering plans actually contain — the
 * right length for its country and a leading digit its country hands out —
 * since `/fiat/*` refuses anything else on any route and the code it sends
 * would otherwise go nowhere. A pattern the step declares narrows that further;
 * it never widens it.
 */
export function isCompleteContact(
  field: SessionContactField,
  value: string,
  region?: string,
): boolean {
  const trimmed = value.trim()
  if (trimmed.length === 0) {
    return false
  }
  const declared = compilePattern(field.regex)
  if (field.kind === 'PHONE') {
    const number = toE164(trimmed, region)
    return number !== undefined && (declared?.test(number) ?? true)
  }
  return declared === undefined
    ? looksLikeEmail(trimmed)
    : declared.test(trimmed)
}

/**
 * The value as the step sends it: a number in E.164, however it was typed, and
 * an address trimmed. What could not be read as a number goes as typed, so the
 * server's own refusal names it rather than a silently altered value.
 */
export function normalizeContact(
  field: SessionContactField,
  value: string,
  region?: string,
): string {
  const trimmed = value.trim()
  return field.kind === 'PHONE' ? (toE164(trimmed, region) ?? trimmed) : trimmed
}

/**
 * The number in E.164 when it is a valid number somewhere — with the buyer's
 * region filling in the country code a locally-dialled number leaves out.
 *
 * The library throws on a country it has never heard of, and the region comes
 * from a lookup, so an unknown one is dropped rather than trusted.
 */
function toE164(value: string, region?: string): string | undefined {
  try {
    const parsed = parsePhoneNumberFromString(value, countryCode(region))
    return parsed?.isValid() ? parsed.number : undefined
  } catch {
    return undefined
  }
}

const ISO_COUNTRY = /^[A-Za-z]{2}$/

function countryCode(region: string | undefined): CountryCode | undefined {
  return region !== undefined && ISO_COUNTRY.test(region)
    ? (region.toUpperCase() as CountryCode)
    : undefined
}

// A pattern the server sends is data, and an unparseable one must not take the
// screen down — the value simply falls back to the local check.
function compilePattern(regex: string | undefined): RegExp | undefined {
  if (regex === undefined) {
    return undefined
  }
  try {
    return new RegExp(regex)
  } catch {
    return undefined
  }
}

/**
 * Deliberately loose — a stricter pattern rejects addresses that are perfectly
 * valid.
 */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}
